import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PageMeta } from '@/components/seo/PageMeta'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ApprovalRequestList } from '@/components/approval'
import { approvalWorkflowService } from '@/lib/api/services'
import type { PendingApproval } from '@/lib/api/types'
import {
  RefreshCw,
  ClipboardCheck,
  Loader2,
  AlertCircle,
  Search,
  Filter,
  Building2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/contexts/auth-context'

interface TenantOption {
  id: string
  name: string
}

export default function PendingApprovalsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tenantId = searchParams.get('tenantId')
  const { user: currentUser } = useAuth()

  const [approvals, setApprovals] = useState<PendingApproval[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<'newest' | 'oldest'>('newest')

  // Company filter state for admins
  const [availableCompanies, setAvailableCompanies] = useState<TenantOption[]>([])
  const [companiesLoading, setCompaniesLoading] = useState(false)

  // Check if user is admin
  const isAdmin = currentUser?.isSuperAdmin || currentUser?.roles?.some(r =>
    r.name === 'Admin' || r.name === 'SuperAdmin' || r.name === 'System Admin' || r.name === 'Administrator'
  )

  const handleCompanyChange = (value: string) => {
    if (value === 'all') {
      searchParams.delete('tenantId')
    } else {
      searchParams.set('tenantId', value)
    }
    setSearchParams(searchParams)
  }

  const loadApprovals = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await approvalWorkflowService.getPendingApprovals(tenantId || undefined)
      setApprovals(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pending approvals')
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    loadApprovals()
  }, [loadApprovals])

  // Load available companies for admin users
  useEffect(() => {
    const loadCompanies = async () => {
      if (!isAdmin) return

      setCompaniesLoading(true)
      try {
        const { tenantsService } = await import('@/lib/api/services/tenants')
        const response = await tenantsService.getTenants({ pageSize: 100, sortBy: 'name' })
        setAvailableCompanies(response.items.map(t => ({ id: t.id, name: t.name })))
      } catch (err) {
        console.error('Failed to load companies:', err)
      } finally {
        setCompaniesLoading(false)
      }
    }

    loadCompanies()
  }, [isAdmin])

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await loadApprovals()
    } finally {
      setRefreshing(false)
    }
  }

  // Filter and sort approvals
  const filteredApprovals = approvals
    .filter((approval) => {
      if (!search) return true
      const searchLower = search.toLowerCase()
      return (
        approval.templateName.toLowerCase().includes(searchLower) ||
        approval.stageName.toLowerCase().includes(searchLower) ||
        approval.submittedByName.toLowerCase().includes(searchLower)
      )
    })
    .sort((a, b) => {
      const dateA = new Date(a.submittedAt).getTime()
      const dateB = new Date(b.submittedAt).getTime()
      return sortBy === 'newest' ? dateB - dateA : dateA - dateB
    })

  return (
    <>
      <PageMeta
        title="My Pending Approvals"
        description="Templates awaiting your review and approval"
      />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {isAdmin && tenantId ? 'Pending Approvals' : 'My Pending Approvals'}
            </h1>
            <p className="text-neutral-500 dark:text-neutral-400 mt-1">
              Templates awaiting your review and approval
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={loading || refreshing}
            >
              <RefreshCw className={cn('h-4 w-4 mr-2', refreshing && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <ClipboardCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm text-amber-800 dark:text-amber-200">
              <span className="font-semibold">{approvals.length}</span> pending approval
              {approvals.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Company Filter for Admins */}
          {isAdmin && availableCompanies.length > 0 && (
            <Select
              value={tenantId || 'all'}
              onValueChange={handleCompanyChange}
              disabled={companiesLoading}
            >
              <SelectTrigger className="w-[200px]">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {availableCompanies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={sortBy} onValueChange={(v) => setSortBy(v as 'newest' | 'oldest')}>
            <SelectTrigger className="w-[180px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Error State */}
        {error && !loading && (
          <div className="p-4 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/50 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={loadApprovals}>
              Retry
            </Button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
            <p className="text-neutral-500 dark:text-neutral-400">Loading pending approvals...</p>
          </div>
        )}

        {/* Content */}
        {!loading && (
          <>
            {approvals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4 border border-dashed border-neutral-200 dark:border-neutral-700 rounded-lg">
                <div className="p-4 rounded-full bg-green-100 dark:bg-green-900/30">
                  <ClipboardCheck className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  All caught up!
                </h3>
                <p className="text-neutral-500 dark:text-neutral-400 text-center max-w-md">
                  {isAdmin && tenantId
                    ? 'No pending approvals for this company.'
                    : "You don't have any pending approvals. Templates requiring your review will appear here."}
                </p>
              </div>
            ) : filteredApprovals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4 border border-dashed border-neutral-200 dark:border-neutral-700 rounded-lg">
                <Search className="h-8 w-8 text-neutral-400" />
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  No matching results
                </h3>
                <p className="text-neutral-500 dark:text-neutral-400 text-center max-w-md">
                  No pending approvals match your search criteria.
                </p>
                <Button variant="outline" onClick={() => setSearch('')}>
                  Clear Search
                </Button>
              </div>
            ) : (
              <ApprovalRequestList
                approvals={filteredApprovals}
                onItemClick={(approval) => navigate(`/approval/${approval.id}`)}
              />
            )}
          </>
        )}
      </div>
    </>
  )
}
